
/**
 * Module dependencies.
 */

import { NavThemeProps, borderOpacity, setNavColor } from './nav-theme';
import { color, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { useLanguageLinks } from './use-language-links';
import { useRouter } from 'next/router';
import React, {
  FocusEvent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import RouterLink from 'src/components/core/links/router-link';
import Svg from 'src/components/core/svg';
import caretIcon from 'src/assets/svg/caret.svg';
import map from 'lodash/map';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = NavThemeProps & {
  className?: string
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  position: relative;
`;

/**
 * `Toggle` styled component.
 */

const Toggle = styled.button<NavThemeProps>`
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  appearance: none;
  background: none;
  border: 0;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  gap: ${units(0.5)};
  letter-spacing: 0.01em;
  line-height: 1.5;
  outline: none;
  padding: 0;
  text-transform: uppercase;
  transition: opacity ${theme('animations.fastTransition')};

  ${setNavColor}

  &:focus-visible,
  &:hover {
    opacity: 0.7;
  }
`;

/**
 * `Caret` styled component.
 */

const Caret = styled.span<{ isOpen: boolean }>`
  line-height: 0;
  transition: transform ${theme('animations.fastTransition')};

  ${ifProp('isOpen', css`
    transform: rotate(180deg);
  `)}
`;

/**
 * `List` styled component.
 */

const List = styled.ul<{ isOpen: boolean }>`
  background-color: ${color('white')};
  border: 1px solid ${color.transparentize('neutralDarkest', borderOpacity)};
  min-width: 100%;
  padding: ${units(1)} 0;
  position: absolute;
  right: 0;
  top: calc(100% + ${units(1)});
  transition: ${theme('animations.fastTransition')};
  transition-property: opacity, visibility;

  ${ifProp('isOpen', css`
    opacity: 1;
    visibility: visible;
  `, css`
    opacity: 0;
    visibility: hidden;
  `)}
`;

/**
 * `ListItemLink` styled component.
 */

const ListItemLink = styled(RouterLink)<{ isActive: boolean }>`
  color: ${color('black')};
  display: block;
  font-size: 12px;
  letter-spacing: 0.01em;
  line-height: 1.5;
  padding: ${units(0.75)} ${units(2)};
  text-transform: uppercase;
  transition: color ${theme('animations.fastTransition')};
  white-space: nowrap;

  &:focus-visible,
  &:hover {
    color: ${color('brown400')};
  }

  ${ifProp('isActive', css`
    font-weight: 600;
  `)}
`;

/**
 * `LanguageDropdown` component.
 */

const LanguageDropdown = (props: Props): ReactElement => {
  const { className, colorTheme } = props;
  const { locale } = useRouter();
  const languageLinks = useLanguageLinks();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const close = useCallback(() => setOpen(false), []);

  // Tabbing past the last language must close the dropdown, the same way
  // clicking away or pressing Escape does.
  const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      close();
    }
  }, [close]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = ({ target }: MouseEvent) => {
      if (!wrapperRef.current?.contains(target as Node)) {
        close();
      }
    };

    const handleKeyDown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') {
        close();
        toggleRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [close, isOpen]);

  return (
    <Wrapper
      className={className}
      onBlur={handleBlur}
      ref={wrapperRef}
    >
      <Toggle
        aria-expanded={isOpen}
        aria-haspopup={'true'}
        aria-label={'Language'}
        colorTheme={colorTheme}
        onClick={() => setOpen(isOpen => !isOpen)}
        ref={toggleRef}
        type={'button'}
      >
        {locale}

        <Caret isOpen={isOpen}>
          <Svg
            icon={caretIcon}
            size={units(2)}
          />
        </Caret>
      </Toggle>

      <List isOpen={isOpen}>
        {map(languageLinks, ({ href, isActive, locale: language }) => (
          <li key={language}>
            <ListItemLink
              href={href}
              isActive={isActive}
              locale={language}
              onClick={close}
            >
              {language}
            </ListItemLink>
          </li>
        ))}
      </List>
    </Wrapper>
  );
};

/**
 * Export `LanguageDropdown` component.
 */

export default LanguageDropdown;
